import { endOfDay, startOfDay } from 'date-fns';
import { DateRange } from '../models/timeline-calendar.types';

interface ClampRangeParams {
  range: DateRange;
  minSpanMs: number;
  maxSpanMs: number;
}

interface CalculateZoomedRangeParams extends ClampRangeParams {
  scale: number;
}

const normalizeSpan = (spanMs: number, minSpanMs: number, maxSpanMs: number): number => {
  return Math.max(minSpanMs, Math.min(maxSpanMs, spanMs));
};

const toRangeFromCenter = (centerMs: number, spanMs: number): DateRange => {
  const half = spanMs / 2;
  return {
    start: startOfDay(new Date(centerMs - half)),
    end: endOfDay(new Date(centerMs + half)),
  };
};

export const clampDateRangeSpan = ({ range, minSpanMs, maxSpanMs }: ClampRangeParams): DateRange => {
  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const currentSpanMs = Math.max(1, endMs - startMs);
  const clampedSpanMs = normalizeSpan(currentSpanMs, minSpanMs, maxSpanMs);

  if (clampedSpanMs === currentSpanMs) {
    return range;
  }

  const centerMs = (startMs + endMs) / 2;
  return toRangeFromCenter(centerMs, clampedSpanMs);
};

export const calculateZoomedRange = ({
  range,
  scale,
  minSpanMs,
  maxSpanMs,
}: CalculateZoomedRangeParams): DateRange => {
  if (!Number.isFinite(scale) || scale <= 0) {
    return range;
  }

  const startMs = range.start.getTime();
  const endMs = range.end.getTime();
  const currentSpanMs = Math.max(1, endMs - startMs);
  const nextSpanMs = normalizeSpan(currentSpanMs / scale, minSpanMs, maxSpanMs);
  const centerMs = (startMs + endMs) / 2;
  const half = nextSpanMs / 2;

  return {
    start: new Date(centerMs - half),
    end: new Date(centerMs + half),
  };
};
